import React, { useCallback, useState, useEffect } from "react";
import Button from "../ui-components/Button";
import Input from "../ui-components/Input";
import Select from "../ui-components/Select";
import getOptionName from "../utils/getOptionName";
import getOptionValue from "../utils/getOptionValue";
import sortBlock from "../utils/sortBlock";
import ArrayOfStringInput from "./ArrayOfStringInput";
import ServiceConfiguratorCompositeQuestionFieldSelector
  from "@components/ServiceConfiguratorCompositeQuestionFieldSelector";
import ServiceConfiguratorCompositeOutputPathSelector
  from "@components/ServiceConfiguratorCompositeOutputPathSelector";
import {FIELDS} from "@feature/services/connettori";
import Alert from "@ui-components/Alert";
import {FormattedMessage} from "react-intl";
import {BlockSelector} from "@components/BlockSelector";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import SearchSelect from "../ui-components/SearchSelect";
import _ from "lodash";

const operators = [
  { value: "===", name: "Equal", multiSelect: true, bool: true },
  { value: "!==", name: "Not equal", multiSelect: true },
  { value: ">", name: "Major" },
  { value: "<", name: "Minor" },
  { value: "in", name: "Contained in" },
  { value: "not in", name: "Not contained in" },
  { value: "exist", name: "Exist", bool: true, multiSelect: true },
  { value: "not exist", name: "Not exist", bool: true, multiSelect: true },
  {value: ">=", name: "Major or equal"},
  {value: "<=", name: "Minor or equal"},
];

const ExpressionValueInput = ({
  updateExpression,
  expressionObject,
  selectedBlock,
  data,
  lockMissingSelectedOptions=false,
  editable=true,
}) => {
  const { operator, value } = expressionObject;

  const [kcolorProducts, setKColorProducts] = useState([]);
  const [kcolorProductPackages, setKColorProductPackages] = useState([]);
  const apiRequest = useCountryAxiosInstance();
  useEffect(async () => {
    if (selectedBlock?.input_type === "k_color_input") {
      const response = await apiRequest.get("/kcolors/products")
      setKColorProducts(response.map((v) => ({
        cod_prod_comm: v.cod_prod_comm,
        des_prod_comm: v.des_prod_comm,
        id: v.id,
        lst_min_qty: v.lst_min_qty,
        base_um: v.base_um,
      })));
      // pick distinct product packages (lst_min_qty)
      const packages = [...new Set(response.flatMap((v) => v.lst_min_qty || []))].sort((a, b) => a - b);
      setKColorProductPackages(packages);
    }
  }, [selectedBlock?.input_type]);

  const onChangeSelect = useCallback(
    (value) => {
      updateExpression({
        ...expressionObject,
        value: value
      });
    },
    [updateExpression, expressionObject]
  );
  switch (operator) {
    case "===":
    case "!==":
    case ">=":
    case "<=":
    case "<":
    case ">": {
      if (selectedBlock?.input_type === "bool") {
        return (
          <Select
            options={[
              { value: "", name: "" },
              { value: true, name: "True" },
              { value: false, name: "False" },
            ]}
            /*
            !e.target.value
          ? e.target.value
          : parse
          ? JSON.parse(e.target.value)
          : e.target.value,
             */
            onChange={(e) => onChangeSelect(
              !e.target.value? e.target.value : JSON.parse(e.target.value)
            )}
            value={value}
            label="Value"
            disabled={!editable}
          />
        );
      }

      if (
        (operator === "!==" || operator === "===") &&
        (selectedBlock?.input_type === "select" ||
          selectedBlock?.input_type === "multi_select")
      ) {
        const options = data
          .filter((d) => d.type === "option" && d.input_id === selectedBlock.id)
          .map((option) => {
            const product = option.product || null;
            return {
              label: getOptionName(option, product),
              value: getOptionValue(option),
            };
          });

        return (
          <BlockSelector
            label="value"
            value={value}
            options={options}
            onChange={(selected) => onChangeSelect(selected?.value)}
            showNotFoundWarning={lockMissingSelectedOptions}
            editable={editable}
          />
        );
      }

      if (
        (operator === "!==" || operator === "===") &&
        (["connector_input"].includes(selectedBlock?.input_type))
      ) {
        const {type} = selectedBlock.metadata_ || {};
        if (!!type) {
          const fieldOptions = FIELDS[type]
            ?.[expressionObject.field]
            ?.options
          || {};
          const options = Object.keys(fieldOptions).map((key) => ({
            name: fieldOptions[key],
            value: key,
          }));
          if (options.length)
            return (
              <Select
                options={[{ value: "", name: "" }, ...options]}
                onChange={(e) => onChangeSelect(e.target.value)}
                value={value}
                label="Value"
                disabled={!editable}
              />
            )
        }
      }

      if (selectedBlock?.input_type === "k_color_input") {
        switch (expressionObject.field) {
          case "kColorProduct":
            return (
              <SearchSelect
                label="KColor Product"
                options={kcolorProducts.map((v) => ({
                  label: `${v.des_prod_comm} (${v.cod_prod_comm})`,
                  value: v.id
                }))}
                isClearable={true}
                value={kcolorProducts.find(p => p.id === value) ? {
                  label: `${kcolorProducts.find(p => p.id === value).des_prod_comm} (${kcolorProducts.find(p => p.id === value).cod_prod_comm})`,
                  value: kcolorProducts.find(p => p.id === value).id
                } : null}
                isDisabled={!editable}
                onChange={(selected) => onChangeSelect(selected?.value)}
              />
            )
          case "productPackage":
            return (
              <SearchSelect
                label="KColor Product Package (min quantity)"
                options={kcolorProductPackages.map((v) => ({
                  label: v.toString(),
                  value: v,
                }))}
                value={kcolorProductPackages.find(p => p === value) ? {
                  label: kcolorProductPackages.find(p => p === value).toString(),
                  value: kcolorProductPackages.find(p => p === value)
                } : null}
                onChange={(selected) => {
                  onChangeSelect(selected?.value)
                }}
                disabled={!editable}
              />
            );
          default:
            throw new Error(`Field ${expressionObject.field} not supported for k_color_input`);
        }

      }

      return (
        <Input
          value={value}
          type={["===", "!=="].indexOf(operator) >= 0 ? "text" : "number"}
          label="Value"
          disabled={!editable}
          onChange={(e) => {
            let value = e.target.value;

            let isNumeric = false;
            if (
              selectedBlock?.input_type === "number_input" ||
              selectedBlock?.type === "output"
            ) {
              isNumeric = true;
            }
            else if (
              selectedBlock?.input_type === "connector_input"
            ) {
              const {type} = selectedBlock.metadata_ || {};
              isNumeric = FIELDS[type]?.[expressionObject.field]?.numeric || false;
            }

            if (isNumeric)
              value = parseFloat(value);

            updateExpression({ ...expressionObject, value });
          }}
        />
      );
    }

    case "in":
    case "not in":
      return (
        <ArrayOfStringInput
          value={value || []}
          label="Values list"
          setValue={(array) =>
            updateExpression({ ...expressionObject, value: array })
          }
          disabled={!editable}
        />
      );

    default:
      return null;
  }
};

const ServiceConfiguratorBlockDependencyExpressionGenerator = ({
  expression,
  questions,
  outputs,
  updateExpression,
  setSelectedOperator,
  data,
  lockMissingSelectedOptions=false,
  editable=true,
  service,
  block,
  fromDesignToQuotation=false,
}) => {

  const isFromDifferentService = block.service_id !== service.id;

  const servicesOptions = data.filter(d => d.type === 'service').map(
    d => ({
      value: d.id,
      label: d.name || `Service block ${d.name} at position ${d.order}`,
    })
  ).concat(
    {
      value: service.id,
      label: service.name,
    }
  )

  const expressionObject =
    expression === "expression" ? {} : JSON.parse(expression);

  const [questionOptions, setQuestionOptions] = useState([]);
  const [outputOptions, setOutputOptions] = useState([]);
  useEffect(() => {
    let outputs_ = [...outputs];
    let questions_ = [...questions];

    if (expressionObject?.serviceBlockId){
      const serviceBlock = data.find(d=>d.id===expressionObject.serviceBlockId);
      outputs_ = outputs_.filter(o=>o.service_id===serviceBlock?.metadata_?.chosen_service_id);
      questions_ = questions_.filter(q=>q.service_id===serviceBlock?.metadata_?.chosen_service_id);
    } else {
      outputs_ = outputs_.filter(o=> isFromDifferentService && !editable ?
        o.service_id === block.service_id :
        o.service_id === service.id || fromDesignToQuotation
      );
      questions_ = questions_.filter(q=>isFromDifferentService && !editable ?
        q.service_id === block.service_id :
        q.service_id === service.id || fromDesignToQuotation
      );
    }

    setOutputOptions([
      ...outputs_
        .filter((o) => ["calculation", "json_output_value"].includes(o.output_type))
        .map((o) => ({
          label: o.name || `Output ${o.id}`,
          value: o.id,
        }))
        .sort((a, b) => sortBlock(a, b, "label")),
    ]);
    setQuestionOptions([
      ...questions_
        .map((q) => ({
          label: q.name || `Question ${q.id}`,
          value: q.id,
        }))
        .sort((a, b) => sortBlock(a, b, "label")),
    ]);
  }, [expressionObject?.serviceBlockId]);


  let selectedBlock = null;

  const listToCheck =
    expressionObject?.blockType === "output" ? outputs : questions;

  if (expressionObject?.inputId) {
    selectedBlock = listToCheck.find((q) => q.id === expressionObject.inputId);
  }

  const getOperator = useCallback(() => {
    if (!selectedBlock?.input_type) {
      return operators;
    }

    switch (selectedBlock.input_type) {
      case "multi_select":
        return operators.filter((op) => op.multiSelect);

      case "bool":
        return operators.filter((op) => op.bool);
      default:
        return operators;
    }
  }, [selectedBlock]);

  const filteredOperators = getOperator();

  return (
    <div className="flex flex-col w-full mb-8 mt-8">
      <div className=" mb-2 flex text-center justify-center">
        <p className="font-bold">EXPRESSION GENERATOR</p>
      </div>

      <Select
        label="Select element type"
        options={[
          { value: "", name: "" },
          { value: "question", name: "Question" },
          { value: "output", name: "Output" },
        ]}
        value={expressionObject.blockType || ""}
        onChange={(e) => updateExpression({ blockType: e.target.value })}
        disabled={!editable}
      />
      {
        servicesOptions.length > 1 && editable &&
        <SearchSelect
          label={"service"}
          options={servicesOptions}
          value={expressionObject?.serviceBlockId?
            servicesOptions.find(o=>o.value===expressionObject.serviceBlockId) :
            servicesOptions.find(o=>o.value===service.id)
          }
          onChange={(e) => {
            if (e.value === service.id) {
              // we want to remove serviceBlockId from expressionObject
              const {serviceBlockId, inputId, field, ...rest} = expressionObject;
              updateExpression(rest);
            } else {
              const {inputId, field, ...rest} = expressionObject;
              updateExpression({
                ...expressionObject,
                serviceBlockId: e.value,
              });
            }
          }}
          isDisabled={!editable}
        />
      }

      {expressionObject?.blockType === "question" && <>
        <BlockSelector
          label="Select question"
          value={expressionObject.inputId}
          options={questionOptions}
          onChange={(selected) => {
            updateExpression({
              blockType: expressionObject.blockType,
              inputId: !!selected?.value ? parseInt(selected.value) : null,
              ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {})
            })
          }}
          showNotFoundWarning={lockMissingSelectedOptions}
          editable={editable}
        />
        <ServiceConfiguratorCompositeQuestionFieldSelector
          label="Question field"
          selectedQuestionId={expressionObject.inputId}
          questions={questions}
          onChange={(e) => {
            updateExpression({
              ...expressionObject,
              field: e.target.value,
            })
          }}
          value={expressionObject.field || ""}
          disabled={!editable}
        />
      </>}

      {expressionObject?.blockType === "output" && <>
        <BlockSelector
          label="Select output"
          value={expressionObject.inputId}
          options={outputOptions}
          onChange={(selected) => {
            updateExpression({
              blockType: expressionObject.blockType,
              inputId: !!selected?.value ? parseInt(selected.value) : null,
              ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {})
            })
          }}
          showNotFoundWarning={lockMissingSelectedOptions}
          editable={editable}
        />
        <ServiceConfiguratorCompositeOutputPathSelector
          label="Select output field"
          selectedOutputId={expressionObject.inputId}
          outputs={outputs}
          onChange={(path) => {
            updateExpression({
              ...expressionObject,
              field: path,
            })
          }}
          value={expressionObject.field || ""}
          editable={editable}
        />
        {!expressionObject.inputId && (
          <Alert
            title={
              <FormattedMessage
                id="dependencies_output_not_found_title"
                defaultMessage="L'output desiderato non compare nella lista?"
              />
            }
            text={
              <FormattedMessage
                id="dependencies_output_not_found_text"
                defaultMessage={`È possibile che l'output in questione sia di tipologia "Question value". In tal caso, è possibile impostare ugualmente l'espressione desiderata selezionando, dal primo menu a tendina di questo riquadro, la tipologia "Question" (per poi selezionare la domanda originale)`}
              />
            }
          />
        )}
      </>}

      {expressionObject?.inputId ? (
        <Select
          label="Select operator"
          options={[{ value: "", name: "" }, ...filteredOperators]}
          value={expressionObject.operator || ""}
          onChange={(e) => {
            if (["exist", "not-exist"].includes(e.target.value)) {
              const { value, ...rest } = expressionObject;
              return updateExpression({
                ...rest,
                operator: e.target.value,
              });
            }
            return updateExpression({
              ...expressionObject,
              operator: e.target.value,
            });
          }}
          disabled={!editable}
        />
      ) : null}
      {expressionObject?.inputId && expressionObject?.operator ? (
        <ExpressionValueInput
          expressionObject={expressionObject}
          updateExpression={updateExpression}
          selectedBlock={selectedBlock}
          data={data}
          lockMissingSelectedOptions={lockMissingSelectedOptions}
          editable={editable}
        />
      ) : null}

      {editable && <div className="mt-4 flex justify-end">
        <Button onClick={() => setSelectedOperator(null)}>DONE</Button>
      </div>}
    </div>
  );
};

export default ServiceConfiguratorBlockDependencyExpressionGenerator;
